import { type CustomCellEditorProps } from '@mint-lib/ag-grid/react';
import type { FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { ChevronDown } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  Chip,
  itemVariants,
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@mint-lib/tw-components';

import useChangeStatusMutation from '../../../api/mutations/useChangeStatusMutation.js';
import { useSetDisableFlowModalContext } from '../../../atoms/disableFlowModal.js';
import {
  getMapChipMetaByFlowStatus,
  READONLY_FLOW_STATUSES,
  type FlowStatuses,
} from '../../../constants/singleFlow.js';
import type { ApprovalTableItemData } from '../../../types/approversTable.js';

type Props = CustomCellEditorProps<ApprovalTableItemData, FlowStatuses>;

const StatusCellEditor: FC<Props> = ({ api, value, data }) => {
  const { t } = useTranslation('@myn/approval_v2');
  const { changeFlowStatus } = useChangeStatusMutation();
  const setDisableFlowModalContext = useSetDisableFlowModalContext();
  const chipMetaMapByFlowStatus = getMapChipMetaByFlowStatus(t);

  const isStatusActive = value === 'active';
  const isStatusInactive = value === 'inactive';
  const isStatusEditable = value
    ? !READONLY_FLOW_STATUSES.includes(value)
    : false;
  const activeChipMeta = chipMetaMapByFlowStatus['active'];
  const inactiveChipMeta = chipMetaMapByFlowStatus['inactive'];

  const chipProps = isStatusActive ? activeChipMeta : inactiveChipMeta;
  const popoverItemLabel = isStatusActive
    ? inactiveChipMeta.label
    : activeChipMeta.label;

  const handlePopoverItemClick = () => {
    updateFlowStatus(isStatusInactive);
    api.stopEditing();
  };

  const updateFlowStatus = async (enable: boolean) => {
    if (!enable && data.inUse) {
      setDisableFlowModalContext({
        isOpened: true,
        flowId: data.id,
      });
      return;
    }

    try {
      await changeFlowStatus({
        flowId: data.id,
        isActive: enable,
      });

      enqueueSnackbar({
        title: enable
          ? t('Flow enabled successfully.')
          : t('Flow disabled successfully.'),
        variant: 'success',
        removable: true,
      });
    } catch {
      enqueueSnackbar({
        title: enable
          ? t('Flow was not enabled.')
          : t('Flow was not disabled.'),
        variant: 'error',
        removable: true,
      });
    }
  };

  if (!isStatusEditable) {
    return null;
  }

  return (
    <Popover open>
      <span className="flex h-full w-full px-4 py-1">
        <PopoverTrigger asChild>
          <Chip
            className="group"
            data-dd-action-name="Show status option list"
            {...chipProps}
          >
            {chipProps.label}
            <ChevronDown className="group-data-[state=open]:rotate-180" />
          </Chip>
        </PopoverTrigger>
      </span>
      <PopoverPortal>
        <PopoverContent align="start" className="z-popover">
          <button
            type="button"
            className={itemVariants({ popoverItem: 'default' })}
            data-dd-action-name="Select option"
            onClick={handlePopoverItemClick}
          >
            {popoverItemLabel}
          </button>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

export default StatusCellEditor;
